import React, {useCallback, useMemo} from 'react';
import {useJobContext} from 'contexts';
import {Icon} from 'shared/helpers';
import {isDeviceSmall, useIsHandheldPortrait} from 'shared/helpers/DeviceSize';
import {Button} from 'react-bootstrap';
import {CartContent} from 'shared/components/Header/CartContent';
import useUI from 'shared/useUI';
import Sidebar, {useRightSidebar} from 'shared/components/Sidebar';
import {SidebarTab} from 'shared/components/Sidebar/SidebarTab';
import {PartialJob} from 'shared/types/PartialJob';
import {CartToggleButton} from 'shared/components/Header/CartToggleButton';
import {SidebarBehaviour, SidebarStatus} from 'store/customer/UISlice';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import {useNavigate} from 'react-router-dom';

export const SidebarRight = () => {
    const navigate = useNavigate();
    const {
        rightSidebar,
        rightSidebarBehaviour,
        alternateCartContent,
        isCartOverlayLayout,
        setRightSidebar,
    } = useUI();
    const {sidebarCartButtonHandler, toggle} = useRightSidebar();
    const jobContext = useJobContext() as PartialJob | undefined;
    const {job, room} = jobContext ?? ({} as PartialJob);
    const isSmallDevice = isDeviceSmall();
    const isHandheldPortrait = useIsHandheldPortrait();
    const {isRoomPlannerPage} = useRoomPlannerLeftMenu();

    const jobDashboardLink = useMemo(() => {
        if (job) {
            if (room && room.id) {
                return `/v2/job/${job.displayId}/room/${room.id}/dashboard`;
            } else {
                return `/v2/job/${job.displayId}/dashboard`;
            }
        }

        return '';
    }, [job, room]);

    const buttonClickHandler = useCallback(() => {
        if (isHandheldPortrait) {
            setRightSidebar(SidebarStatus.CLOSED);
        }

        void navigate(jobDashboardLink);
    }, [jobDashboardLink, navigate, isHandheldPortrait, setRightSidebar]);

    const cartHeader = useMemo(() => {
        return (
            <>
                <div className="text-right">
                    <Button
                        onClick={buttonClickHandler}
                        variant="link"
                        id="collapse-cart">
                        Go to Dashboard
                    </Button>
                </div>
                {alternateCartContent ? (
                    <>
                        <Icon
                            iconName="Layout.svg"
                            className="pointer"
                            onClick={sidebarCartButtonHandler}
                        />
                        <label>Layout Options</label>
                    </>
                ) : (
                    <CartToggleButton showOnOpen={true} />
                )}
            </>
        );
    }, [job, rightSidebar, alternateCartContent, buttonClickHandler]);

    if (rightSidebar && (isRoomPlannerPage || !isSmallDevice)) {
        const softOpen =
            isCartOverlayLayout ||
            rightSidebarBehaviour == SidebarBehaviour.SOFT_OPEN;

        return (
            <Sidebar
                sidebarId="sidebar-right"
                className="sidebar-right"
                anchor="RIGHT"
                behaviour={rightSidebarBehaviour}
                status={rightSidebar}
                header={cartHeader}
                showOverlays={false}
                softOpen={softOpen}
                style={
                    rightSidebarBehaviour == SidebarBehaviour.TOGGLE &&
                    !softOpen
                        ? {position: 'relative', flexShrink: 0}
                        : {flexShrink: 0}
                }>
                <>
                    <SidebarTab
                        $anchor="RIGHT"
                        type="button"
                        title="Close cart"
                        aria-label="Close cart"
                        onClick={toggle}>
                        <Icon iconName="chevron-right.svg" />
                    </SidebarTab>
                    {alternateCartContent ? (
                        alternateCartContent
                    ) : (
                        <CartContent />
                    )}
                </>
            </Sidebar>
        );
    } else {
        return null;
    }
};
